import { useState, useCallback, useRef, useEffect } from 'react';
import type { WizardData } from '../ActorGenerationWizard';
import styles from './Steps.module.css';

type Props = {
  data: WizardData;
  onUpdate: (updates: Partial<WizardData>) => void;
};

type CropArea = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export function FaceCropStep({ data, onUpdate }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const [cropArea, setCropArea] = useState<CropArea>(() => {
    // デフォルトのクロップ領域（右上の顔部分を想定）
    return data.faceCropArea || { x: 0.7, y: 0.05, width: 0.25, height: 0.4 };
  });

  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [dragType, setDragType] = useState<'move' | 'resize' | null>(null);
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [displaySize, setDisplaySize] = useState({ width: 0, height: 0 });

  // 画像をロードして表示
  useEffect(() => {
    if (!data.characterSheetBase64) return;

    const img = new Image();
    img.onload = () => {
      imageRef.current = img;
      setImageSize({ width: img.width, height: img.height });

      // 表示サイズを計算（コンテナに収まるように）
      const container = containerRef.current;
      if (container) {
        const maxWidth = container.clientWidth - 40;
        const maxHeight = 500;
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        setDisplaySize({
          width: img.width * ratio,
          height: img.height * ratio,
        });
      }
    };
    img.src = `data:${data.characterSheetMimeType};base64,${data.characterSheetBase64}`;
  }, [data.characterSheetBase64, data.characterSheetMimeType]);

  // キャンバスを描画
  useEffect(() => {
    const canvas = canvasRef.current;
    const img = imageRef.current;
    if (!canvas || !img || displaySize.width === 0) return;

    canvas.width = displaySize.width;
    canvas.height = displaySize.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // 画像を描画
    ctx.drawImage(img, 0, 0, displaySize.width, displaySize.height);

    // オーバーレイを描画（暗くする）
    ctx.fillStyle = 'rgba(0, 0, 0, 0.5)';
    ctx.fillRect(0, 0, displaySize.width, displaySize.height);

    // クロップ領域（正円）
    const cropX = cropArea.x * displaySize.width;
    const cropY = cropArea.y * displaySize.height;
    const cropW = cropArea.width * displaySize.width;
    const cropH = cropArea.height * displaySize.height;
    const centerX = cropX + cropW / 2;
    const centerY = cropY + cropH / 2;
    const radius = Math.min(cropW, cropH) / 2;

    // 円形の領域をクリア（明るく見せる）
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // クロップ領域の元画像を円形で再描画
    ctx.save();
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.clip();
    ctx.drawImage(
      img,
      cropArea.x * img.width,
      cropArea.y * img.height,
      cropArea.width * img.width,
      cropArea.height * img.height,
      cropX,
      cropY,
      cropW,
      cropH
    );
    ctx.restore();

    // クロップ領域の枠線（円形）
    ctx.strokeStyle = '#6366f1';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();

    // リサイズハンドル（右下角）
    const handleSize = 10;
    ctx.fillStyle = '#6366f1';
    ctx.fillRect(cropX + cropW - handleSize / 2, cropY + cropH - handleSize / 2, handleSize, handleSize);
  }, [cropArea, displaySize]);

  // ポインター座標を取得（マウス/タッチ共通）
  const getPointerPosition = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return null;

    const rect = canvas.getBoundingClientRect();
    let clientX: number, clientY: number;

    if ('touches' in e) {
      // タッチイベント
      if (e.touches.length === 0) return null;
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      // マウスイベント
      clientX = e.clientX;
      clientY = e.clientY;
    }

    return {
      x: (clientX - rect.left) / displaySize.width,
      y: (clientY - rect.top) / displaySize.height,
    };
  }, [displaySize]);

  // ポインターダウン（マウス/タッチ共通）
  const handlePointerDown = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    const pos = getPointerPosition(e);
    if (!pos) return;

    // タッチイベントの場合、デフォルト動作を防止（スクロール防止）
    if ('touches' in e) {
      e.preventDefault();
    }

    const { x, y } = pos;

    // リサイズハンドルのクリック判定（タッチ用に判定範囲を広げる）
    const handleSize = 'touches' in e ? 0.05 : 0.02;
    const handleX = cropArea.x + cropArea.width;
    const handleY = cropArea.y + cropArea.height;

    if (
      Math.abs(x - handleX) < handleSize &&
      Math.abs(y - handleY) < handleSize
    ) {
      setIsDragging(true);
      setDragType('resize');
      setDragStart({ x, y });
      return;
    }

    // クロップ領域内のクリック判定
    if (
      x >= cropArea.x &&
      x <= cropArea.x + cropArea.width &&
      y >= cropArea.y &&
      y <= cropArea.y + cropArea.height
    ) {
      setIsDragging(true);
      setDragType('move');
      setDragStart({ x: x - cropArea.x, y: y - cropArea.y });
    }
  }, [cropArea, getPointerPosition]);

  // ポインタームーブ（マウス/タッチ共通）
  const handlePointerMove = useCallback((e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>) => {
    if (!isDragging) return;

    // タッチイベントの場合、デフォルト動作を防止
    if ('touches' in e) {
      e.preventDefault();
    }

    const pos = getPointerPosition(e);
    if (!pos) return;

    const { x, y } = pos;

    if (dragType === 'move') {
      const newX = Math.max(0, Math.min(1 - cropArea.width, x - dragStart.x));
      const newY = Math.max(0, Math.min(1 - cropArea.height, y - dragStart.y));
      setCropArea((prev) => ({ ...prev, x: newX, y: newY }));
    } else if (dragType === 'resize') {
      // 正方形に固定：幅と高さの大きい方を採用
      const rawWidth = x - cropArea.x;
      const rawHeight = y - cropArea.y;

      // ピクセル単位で正方形にするため、アスペクト比を考慮
      const aspectRatio = imageSize.width / imageSize.height;

      // 変化量の大きい方を採用（正規化座標で比較）
      let newWidth: number;
      if (rawWidth * aspectRatio > rawHeight) {
        newWidth = rawWidth;
      } else {
        newWidth = rawHeight / aspectRatio;
      }

      // 範囲チェック（最小3%）
      newWidth = Math.max(0.03, Math.min(1 - cropArea.x, newWidth));

      // ピクセル単位で正方形になるように高さを計算
      let newHeight = newWidth * aspectRatio;

      // 高さが範囲外なら調整
      if (cropArea.y + newHeight > 1) {
        newHeight = 1 - cropArea.y;
        newWidth = newHeight / aspectRatio;
      }

      setCropArea((prev) => ({ ...prev, width: newWidth, height: newHeight }));
    }
  }, [isDragging, dragType, dragStart, cropArea, imageSize, getPointerPosition]);

  // ポインターアップ（マウス/タッチ共通）
  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setDragType(null);
  }, []);

  // クロップを適用して顔画像を生成（正円）
  const applyCrop = useCallback(() => {
    const img = imageRef.current;
    if (!img || !data.characterSheetBase64) return;

    // オフスクリーンキャンバスでクロップ
    const cropCanvas = document.createElement('canvas');
    const cropWidth = Math.round(cropArea.width * img.width);
    const cropHeight = Math.round(cropArea.height * img.height);
    // 正方形のキャンバス（円形クロップ用）
    const size = Math.min(cropWidth, cropHeight);
    cropCanvas.width = size;
    cropCanvas.height = size;

    const ctx = cropCanvas.getContext('2d');
    if (!ctx) return;

    // 円形にクリップ
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
    ctx.clip();

    // ソース画像の中心から正方形を切り出し
    const srcX = Math.round(cropArea.x * img.width) + (cropWidth - size) / 2;
    const srcY = Math.round(cropArea.y * img.height) + (cropHeight - size) / 2;

    ctx.drawImage(
      img,
      srcX,
      srcY,
      size,
      size,
      0,
      0,
      size,
      size
    );

    // Base64に変換
    const faceBase64 = cropCanvas.toDataURL('image/png').split(',')[1];

    onUpdate({
      faceImageBase64: faceBase64,
      faceCropArea: cropArea,
    });
  }, [cropArea, data.characterSheetBase64, onUpdate]);

  // クロップ領域が変更されたら自動適用
  useEffect(() => {
    if (imageRef.current) {
      applyCrop();
    }
  }, [cropArea]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.stepContainer}>
      <p className={styles.cropHelpTextTop}>
        円形の枠をドラッグして顔の位置を調整してください
      </p>

      <div className={styles.cropContainer} ref={containerRef}>
        {data.characterSheetBase64 ? (
          <canvas
            ref={canvasRef}
            className={styles.cropCanvas}
            onMouseDown={handlePointerDown}
            onMouseMove={handlePointerMove}
            onMouseUp={handlePointerUp}
            onMouseLeave={handlePointerUp}
            onTouchStart={handlePointerDown}
            onTouchMove={handlePointerMove}
            onTouchEnd={handlePointerUp}
            onTouchCancel={handlePointerUp}
            style={{ cursor: isDragging ? 'grabbing' : 'grab', touchAction: 'none' }}
          />
        ) : (
          <div className={styles.noImage}>
            キャラクターシートが生成されていません
          </div>
        )}
      </div>

      {/* クロップ結果プレビュー */}
      <div className={styles.facePreviewContainer}>
        <span className={styles.facePreviewLabel}>顔写真</span>
        <div className={styles.facePreviewSmall}>
          {data.faceImageBase64 ? (
            <img
              src={`data:image/png;base64,${data.faceImageBase64}`}
              alt="顔画像"
            />
          ) : (
            <div className={styles.facePreviewPlaceholder} />
          )}
        </div>
      </div>
    </div>
  );
}
